import React, { useState, useEffect, useCallback } from 'react';
import './NotificationCenter.css';
import { 
  fetchNotifications, 
  fetchNotificationSummary, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  fetchSubscriptions,
  createSubscription,
  deleteSubscription,
  fetchAvailableContacts,
  fetchAvailableServices,
  fetchAvailableStatuses,
  fetchAvailableWorkOrderServices,
  fetchAvailableWorkOrderStatuses,
  fetchUsers
} from './api';
import PWANotificationSetup from './PWANotificationSetup';
import { getRelativeTime } from '../utils/timezone';
import { useNavigate } from 'react-router-dom';
import customerLabel from './customers/customerLabel';

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'unread', 'subscriptions'
  const navigate = useNavigate();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchNotificationsData();
    }
  }, [isOpen, activeTab, currentPage]);

  // Close notification center when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.notification-center')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Reset to page 1 when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.hasPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  const fetchNotificationSummaryData = useCallback(async () => {
    try {
      const response = await fetchNotificationSummary();
      setUnreadCount(response.total_unread);
    } catch (error) {
      console.error('Error fetching notification summary:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotificationSummaryData();
    
    // Handle page visibility to stop polling when tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, stop polling
        return;
      } else {
        // Page is visible, fetch latest notifications
        fetchNotificationSummaryData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Smart polling: Less frequent when notification center is closed
    let interval;
    if (isOpen) {
      // Poll every 2 minutes when notification center is open
      interval = setInterval(fetchNotificationSummaryData, 120000);
    } else {
      // Poll every 10 minutes when notification center is closed
      interval = setInterval(fetchNotificationSummaryData, 600000);
    }
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, fetchNotificationSummaryData]);

  const fetchNotificationsData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab === 'unread') {
        params.is_read = 'false';
      }
      
      const response = await fetchNotifications(params, currentPage, 50);
      
      if (response && response.results) {
        // Check for push trigger notifications and show local notifications
        response.results.forEach(notification => {
          if (notification.notification_type === 'push_trigger' && 
              notification.context_data?.trigger_local) {
            // Show local notification using service worker
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
              navigator.serviceWorker.controller.postMessage({
                type: 'SHOW_NOTIFICATION',
                title: notification.title,
                body: notification.message,
                data: notification.context_data
              });
            }
          }
        });
        
        setNotifications(response.results);
        setTotalPages(response.total_pages || 1);
        setTotalCount(response.count || 0);
        setPaginationInfo({
          hasNext: response.has_next || false,
          hasPrevious: response.has_previous || false,
          nextPage: response.next_page || null,
          previousPage: response.previous_page || null
        });
      } else {
        // Handle case where response doesn't have expected structure
        setNotifications(response || []);
        setTotalPages(1);
        setTotalCount(0);
        setPaginationInfo({
          hasNext: false,
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setTotalPages(1);
      setTotalCount(0);
      setPaginationInfo({
        hasNext: false,
        hasPrevious: false,
        nextPage: null,
        previousPage: null
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true, read_at: new Date().toISOString() }
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleNotificationClick = (notification, event) => {
    // Prevent event bubbling to avoid double-click issues
    event.preventDefault();
    event.stopPropagation();
    
    // Mark as read if not already read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Close notification center
    setIsOpen(false);
    
    // Sales inbox notifications
    if (notification.context_data?.inquiry_id) {
      navigate(`/inbox?inquiry=${notification.context_data.inquiry_id}`);
    }
    // Handle job notifications
    else if (notification.job_id) {
      localStorage.setItem('selectedJobId', notification.job_id);
      // Dispatch custom event to notify JobList component
      window.dispatchEvent(new CustomEvent('jobSelected', { 
        detail: { jobId: notification.job_id } 
      }));
      // Navigate to jobs page
      navigate('/jobs');
    }
    // Handle work order notifications
    else if (notification.work_order_id) {
      localStorage.setItem('selectedWorkOrderId', notification.work_order_id);
      // Dispatch custom event to notify WorkOrderList component
      window.dispatchEvent(new CustomEvent('workOrderSelected', { 
        detail: { workOrderId: notification.work_order_id } 
      }));
      // Navigate to work orders page
      navigate('/');
    }
  };

  const formatDate = (dateString) => {
    return getRelativeTime(dateString);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'job_created':
        return '🆕';
      case 'job_updated':
        return '✏️';
      case 'job_status_changed':
        return '🔄';
      case 'job_assigned':
        return '👤';
      case 'workorder_created':
        return '🆕';
      case 'workorder_updated':
        return '✏️';
      case 'workorder_status_changed':
        return '🔄';
      case 'workorder_deleted':
        return '🗑️';
      default:
        return '📋';
    }
  };

  // No need for client-side filtering since we handle it server-side
  const filteredNotifications = notifications;

  return (
    <div className="notification-center">
      <button 
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div className="notification-tabs">
              <button 
                className={activeTab === 'all' ? 'active' : ''}
                onClick={() => setActiveTab('all')}
              >
                All
              </button>
              <button 
                className={activeTab === 'unread' ? 'active' : ''}
                onClick={() => setActiveTab('unread')}
              >
                Unread ({unreadCount})
              </button>
              <button 
                className={activeTab === 'subscriptions' ? 'active' : ''}
                onClick={() => setActiveTab('subscriptions')}
              >
                Subscriptions
              </button>
            </div>
            {unreadCount > 0 && (
              <button className="mark-all-read-btn" onClick={markAllAsRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-content">
            {activeTab === 'subscriptions' ? (
              <div>
                <div className="mobile-only">
                  <PWANotificationSetup />
                </div>
                <SubscriptionManager />
              </div>
            ) : (
              <>
                {loading ? (
                  <div className="loading">Loading notifications...</div>
                ) : filteredNotifications.length === 0 ? (
                  <div className="no-notifications">
                    {activeTab === 'unread' ? 'No unread notifications' : 'No notifications'}
                  </div>
                ) : (
                  <>
                    <div className="notification-list">
                      {filteredNotifications.map(notification => (
                        <div 
                          key={notification.id} 
                          className={`notification-item ${!notification.is_read ? 'unread' : ''} clickable`}
                          onClick={(event) => handleNotificationClick(notification, event)}
                        >
                          <div className="notification-icon">
                            {getNotificationIcon(notification.notification_type)}
                          </div>
                          <div className="notification-content">
                            <div className="notification-title">{notification.title}</div>
                            <div className="notification-message">{notification.message}</div>
                            <div className="notification-meta">
                              <span className="job-info">
                                {notification.job_id ? (
                                  <>Job #{notification.job_id} - {notification.job_customer_name}</>
                                ) : notification.work_order_id ? (
                                  <>Work Order #{notification.work_order_id} - {notification.work_order_customer_name}</>
                                ) : (
                                  <>Notification</>
                                )}
                              </span>
                              <span className="notification-time">
                                {formatDate(notification.created_at)}
                              </span>
                            </div>
                          </div>
                          {!notification.is_read && <div className="unread-indicator"></div>}
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="notification-pagination">
                        <div className="notification-pagination-info">
                          Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalCount)} of {totalCount} notifications
                        </div>
                        <div className="notification-pagination-controls">
                          <button 
                            className="notification-pagination-btn"
                            onClick={handlePreviousPage}
                            disabled={!paginationInfo.hasPrevious}
                          >
                            ← Previous
                          </button>
                          
                          <div className="notification-pagination-pages">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              
                              return (
                                <button
                                  key={pageNum}
                                  className={`notification-pagination-page ${
                                    pageNum === currentPage ? 'active' : ''
                                  }`}
                                  onClick={() => handlePageChange(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>
                          
                          <button 
                            className="notification-pagination-btn"
                            onClick={handleNextPage}
                            disabled={!paginationInfo.hasNext}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Subscription Manager Component
const SubscriptionManager = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionsData();
  }, []);

  const fetchSubscriptionsData = async () => {
    setLoading(true);
    try {
      const response = await fetchSubscriptions();
      setSubscriptions(response);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteSubscriptionHandler = async (subscriptionId) => {
    try {
      await deleteSubscription(subscriptionId);
      setSubscriptions(prev => prev.filter(sub => sub.id !== subscriptionId));
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  const getSubscriptionDescription = (subscription) => {
    switch (subscription.subscription_type) {
      case 'all_jobs':
        return 'All jobs';
      case 'specific_job':
        return `Job #${subscription.job_id} - ${subscription.job_customer_name}`;
      case 'contact_jobs':
        return `Jobs with contact: ${subscription.contact}`;
      case 'customer_jobs':
        return `Jobs for customer: ${subscription.customer_name}`;
      case 'service_jobs':
        return `Jobs with service: ${subscription.service}`;
      case 'status_jobs':
        return `Jobs with status: ${subscription.status}`;
      case 'all_workorders':
        return 'All work orders';
      case 'specific_workorder':
        return `Work Order #${subscription.work_order_id} - ${subscription.work_order_customer_name}`;
      case 'customer_workorders':
        return `Work orders for customer: ${subscription.customer_name}`;
      case 'service_workorders':
        return `Work orders with service: ${subscription.service}`;
      case 'status_workorders':
        return `Work orders with status: ${subscription.status}`;
      default:
        return subscription.subscription_type;
    }
  };

  return (
    <div className="subscription-manager">
      <div className="subscription-header">
        <h4>Notification Subscriptions</h4>
        <button 
          className="create-subscription-btn"
          onClick={() => setShowCreateForm(true)}
        >
          + Add Subscription
        </button>
      </div>

      {showCreateForm && (
        <CreateSubscriptionForm 
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            fetchSubscriptionsData();
          }}
        />
      )}

      {loading ? (
        <div className="loading">Loading subscriptions...</div>
      ) : subscriptions.length === 0 ? (
        <div className="no-subscriptions">
          No subscriptions yet. Create one to start receiving notifications!
        </div>
      ) : (
        <div className="subscription-list">
          {subscriptions.map(subscription => (
            <div key={subscription.id} className="subscription-item">
              <div className="subscription-info">
                <div className="subscription-description">
                  {getSubscriptionDescription(subscription)}
                </div>
                <div className="subscription-settings">
                  {subscription.notify_on_create && <span className="setting-tag">Create</span>}
                  {subscription.notify_on_update && <span className="setting-tag">Update</span>}
                  {subscription.notify_on_status_change && <span className="setting-tag">Status</span>}
                </div>
              </div>
              <button 
                className="delete-subscription-btn"
                onClick={() => deleteSubscriptionHandler(subscription.id)}
                title="Delete subscription"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Create Subscription Form Component
const CreateSubscriptionForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    subscription_type: 'all_jobs',
    job_id: '',
    work_order_id: '',
    contact: '',
    customer_id: '',
    service: '',
    status: '',
    notify_on_create: true,
    notify_on_update: true,
    notify_on_status_change: true,
  });
  const [availableContacts, setAvailableContacts] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableWorkOrderServices, setAvailableWorkOrderServices] = useState([]);
  const [availableWorkOrderStatuses, setAvailableWorkOrderStatuses] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAvailableOptions();
  }, []);

  const fetchAvailableOptions = async () => {
    try {
      const [contactsRes, servicesRes, statusesRes, woServicesRes, woStatusesRes, customersRes] = await Promise.all([
        fetchAvailableContacts(),
        fetchAvailableServices(),
        fetchAvailableStatuses(),
        fetchAvailableWorkOrderServices(),
        fetchAvailableWorkOrderStatuses(),
        fetchUsers('', 1, 1000), // Fetch customers (fetchUsers actually fetches customers)
      ]);
      
      setAvailableContacts(contactsRes.contacts);
      setAvailableServices(servicesRes.services);
      setAvailableStatuses(statusesRes.statuses);
      setAvailableWorkOrderServices(woServicesRes.services);
      setAvailableWorkOrderStatuses(woStatusesRes.statuses);
      setAvailableCustomers(customersRes.results || []);
    } catch (error) {
      console.error('Error fetching available options:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        subscription_type: formData.subscription_type,
        notify_on_create: formData.notify_on_create,
        notify_on_update: formData.notify_on_update,
        notify_on_status_change: formData.notify_on_status_change,
      };

      // Add specific fields based on subscription type
      if (formData.subscription_type === 'specific_job' && formData.job_id) {
        payload.job_id = parseInt(formData.job_id);
      } else if (formData.subscription_type === 'specific_workorder' && formData.work_order_id) {
        payload.work_order_id = parseInt(formData.work_order_id);
      } else if (formData.subscription_type === 'contact_jobs' && formData.contact) {
        payload.contact = formData.contact;
      } else if (formData.subscription_type === 'customer_jobs' && formData.customer_id) {
        payload.customer_id = parseInt(formData.customer_id);
      } else if (formData.subscription_type === 'customer_workorders' && formData.customer_id) {
        payload.customer_id = parseInt(formData.customer_id);
      } else if (formData.subscription_type === 'service_jobs' && formData.service) {
        payload.service = formData.service;
      } else if (formData.subscription_type === 'service_workorders' && formData.service) {
        payload.service = formData.service;
      } else if (formData.subscription_type === 'status_jobs' && formData.status) {
        payload.status = formData.status;
      } else if (formData.subscription_type === 'status_workorders' && formData.status) {
        payload.status = formData.status;
      }

      await createSubscription(payload);
      onSuccess();
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('Error creating subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear customer search when subscription type changes
    if (name === 'subscription_type') {
      setCustomerSearch('');
    }
  };

  return (
    <div className="create-subscription-form">
      <h4>Create New Subscription</h4>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Subscription Type:</label>
          <select 
            name="subscription_type" 
            value={formData.subscription_type}
            onChange={handleInputChange}
          >
            <optgroup label="Jobs">
              <option value="all_jobs">All Jobs</option>
              <option value="specific_job">Specific Job</option>
              <option value="contact_jobs">Jobs by Contact</option>
              <option value="customer_jobs">Jobs by Customer</option>
              <option value="service_jobs">Jobs by Service</option>
              <option value="status_jobs">Jobs by Status</option>
            </optgroup>
            <optgroup label="Work Orders">
              <option value="all_workorders">All Work Orders</option>
              <option value="specific_workorder">Specific Work Order</option>
              <option value="customer_workorders">Work Orders by Customer</option>
              <option value="service_workorders">Work Orders by Service</option>
              <option value="status_workorders">Work Orders by Status</option>
            </optgroup>
          </select>
        </div>

        {formData.subscription_type === 'specific_job' && (
          <div className="form-group">
            <label>Job ID:</label>
            <input 
              type="number" 
              name="job_id" 
              value={formData.job_id}
              onChange={handleInputChange}
              placeholder="Enter job ID"
            />
          </div>
        )}

        {formData.subscription_type === 'specific_workorder' && (
          <div className="form-group">
            <label>Work Order ID:</label>
            <input 
              type="number" 
              name="work_order_id" 
              value={formData.work_order_id}
              onChange={handleInputChange}
              placeholder="Enter work order ID"
            />
          </div>
        )}

        {formData.subscription_type === 'contact_jobs' && (
          <div className="form-group">
            <label>Contact:</label>
            <select 
              name="contact" 
              value={formData.contact}
              onChange={handleInputChange}
            >
              <option value="">Select contact</option>
              {availableContacts.map(contact => (
                <option key={contact} value={contact}>{contact}</option>
              ))}
            </select>
          </div>
        )}

        {(formData.subscription_type === 'customer_jobs' || formData.subscription_type === 'customer_workorders') && (
          <div className="form-group">
            <label>Customer:</label>
            <input
              type="text"
              placeholder="Search customers..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="customer-search-input"
            />
            <select 
              name="customer_id" 
              value={formData.customer_id}
              onChange={handleInputChange}
              size="5"
              className="customer-select-list"
            >
              <option value="">Select customer</option>
              {availableCustomers
                .filter(customer => 
                  customer.name.toLowerCase().includes(customerSearch.toLowerCase())
                )
                .map(customer => (
                  <option key={customer.id} value={customer.id}>{customerLabel(customer)}</option>
                ))
              }
            </select>
            {formData.customer_id && (
              <div className="selected-customer">
                Selected: {availableCustomers.find(c => c.id === parseInt(formData.customer_id))?.name}
              </div>
            )}
          </div>
        )}

        {formData.subscription_type === 'service_jobs' && (
          <div className="form-group">
            <label>Service:</label>
            <select 
              name="service" 
              value={formData.service}
              onChange={handleInputChange}
            >
              <option value="">Select service</option>
              {availableServices.map(service => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </div>
        )}

        {formData.subscription_type === 'service_workorders' && (
          <div className="form-group">
            <label>Service:</label>
            <select 
              name="service" 
              value={formData.service}
              onChange={handleInputChange}
            >
              <option value="">Select service</option>
              {availableWorkOrderServices.map(service => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </div>
        )}

        {formData.subscription_type === 'status_jobs' && (
          <div className="form-group">
            <label>Status:</label>
            <select 
              name="status" 
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="">Select status</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        )}

        {formData.subscription_type === 'status_workorders' && (
          <div className="form-group">
            <label>Status:</label>
            <select 
              name="status" 
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="">Select status</option>
              {availableWorkOrderStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Notify me when:</label>
          <div className="checkbox-group">
            <label>
              <input 
                type="checkbox" 
                name="notify_on_create" 
                checked={formData.notify_on_create}
                onChange={handleInputChange}
              />
              {formData.subscription_type.includes('workorder') ? 'Work order is created' : 'Job is created'}
            </label>
            <label>
              <input 
                type="checkbox" 
                name="notify_on_update" 
                checked={formData.notify_on_update}
                onChange={handleInputChange}
              />
              {formData.subscription_type.includes('workorder') ? 'Work order is updated' : 'Job is updated'}
            </label>
            <label>
              <input 
                type="checkbox" 
                name="notify_on_status_change" 
                checked={formData.notify_on_status_change}
                onChange={handleInputChange}
              />
              {formData.subscription_type.includes('workorder') ? 'Work order status changes' : 'Job status changes'}
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Subscription'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NotificationCenter;
